import { iconAssetPath } from "@/infrastructure/assets/paths";
import type { IconName, MonBlankName, MonName } from "./icon-names";
import "./Icon.css";

interface IconProps {
  readonly name: IconName | MonName | MonBlankName | "enso";
  /** アイコンの一辺（px）。既定24（アイコンのグリッドに合わせる）。 */
  readonly size?: number;
  readonly className?: string;
  /** 装飾目的（隣に説明テキストがある等）ならtrue。既定はfalseで`role="img"`を付与する。 */
  readonly decorative?: boolean;
  readonly title?: string;
}

function isPaintedGraphic(name: IconProps["name"]): boolean {
  return name === "enso" || name.startsWith("mon-");
}

/**
 * 生成 PNG を表示する。線アイコンは `currentColor` のマスク、
 * 家紋・円相は色を残した画像として出す（D-112）。絵文字は使わない（D-110）。
 */
export function Icon({ name, size = 24, className, decorative = false, title }: IconProps) {
  const src = iconAssetPath(name);
  const painted = isPaintedGraphic(name);
  const classNames = ["icon", painted ? "icon--painted" : null, className]
    .filter(Boolean)
    .join(" ");
  const a11y = decorative
    ? { "aria-hidden": true as const }
    : { role: "img" as const, "aria-label": title ?? name };

  if (painted) {
    return (
      <img
        src={src}
        width={size}
        height={size}
        className={classNames}
        alt={decorative ? "" : (title ?? name)}
        aria-hidden={decorative ? true : undefined}
      />
    );
  }

  return (
    <span
      className={classNames}
      style={{
        width: size,
        height: size,
        maskImage: `url(${src})`,
        WebkitMaskImage: `url(${src})`,
      }}
      {...a11y}
    />
  );
}
